import React, { useRef, useEffect } from "react";
import { motion, useScroll, useTransform, useMotionTemplate } from "framer-motion";
import { useLang } from "@/i18n";
import { useCursor } from "@/context/CursorContext";

const EASE = [0.76, 0, 0.24, 1];

const STATEMENT = [
  { text: "I MAKE", align: "left" },
  { text: "FRAMES", align: "center", gradient: true },
  { text: "MOVE",   align: "right" },
];

const FRAMES_LETTERS = ["F", "R", "A", "M", "E", "S"];

/**
 * FRAMES rendered as inline SVG so both fill AND stroke can share the same
 * animated navy gradient (they read as a single unified surface, not two
 * layers). Stroke-width is smoothly animated per-letter (staggered L→R
 * ripple) — SVG stroke-width interpolates as a real number so the
 * thickening is gradual, never snapping.
 *
 * Magnetic gradient: on mousemove the gradient stops slide toward the
 * cursor's horizontal position over the word, so the highlight follows the
 * pointer for a subtle, alive interaction.
 */
const FramesLogo = () => {
  const svgRef = useRef(null);
  const gradRef = useRef(null);
  const cursorX = useRef(0.5); // 0..1 target
  const currentX = useRef(0.5); // eased
  const rafRef = useRef(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const onMove = (e) => {
      const rect = svg.getBoundingClientRect();
      // clamp so gradient still shows even outside the word
      const x = (e.clientX - rect.left) / rect.width;
      cursorX.current = Math.max(-0.2, Math.min(1.2, x));
    };

    const tick = () => {
      // ease toward target for buttery motion
      currentX.current += (cursorX.current - currentX.current) * 0.12;
      const g = gradRef.current;
      if (g) {
        // Highlight (#5c5cf0 stop) sits at currentX; edges fall to deep navy.
        const c = currentX.current;
        const stops = g.querySelectorAll("stop");
        // 5 stops: 0, c-0.35, c, c+0.35, 1
        const positions = [
          0,
          Math.max(0, c - 0.35),
          Math.max(0.001, Math.min(0.999, c)),
          Math.min(1, c + 0.35),
          1,
        ];
        stops.forEach((s, i) => s.setAttribute("offset", positions[i]));
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <span className="frames-svg-wrap" aria-label="FRAMES">
      <svg
        ref={svgRef}
        viewBox="0 0 720 140"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        focusable="false"
      >
        <defs>
          <linearGradient
            ref={gradRef}
            id="framesGrad"
            x1="0%" y1="0%" x2="100%" y2="0%"
          >
            <stop offset="0"    stopColor="#000080" />
            <stop offset="0.25" stopColor="#2a2ab8" />
            <stop offset="0.5"  stopColor="#5c5cf0" />
            <stop offset="0.75" stopColor="#2a2ab8" />
            <stop offset="1"    stopColor="#000080" />
          </linearGradient>
        </defs>
        <text
          x="360"
          y="118"
          textAnchor="middle"
          fill="url(#framesGrad)"
          stroke="url(#framesGrad)"
          strokeLinejoin="miter"
          strokeMiterlimit="8"
          style={{ paintOrder: "stroke fill", fontSize: 145, letterSpacing: -6 }}
          className="font-display font-medium uppercase"
        >
          {FRAMES_LETTERS.map((ch, i) => (
            <tspan
              key={i}
              className="frames-letter-svg"
              style={{ animationDelay: `${i * 0.12}s` }}
            >
              {ch}
            </tspan>
          ))}
        </text>
      </svg>
    </span>
  );
};

const MaskedLine = ({ children, delay = 0, align = "left", className = "" }) => {
  const alignClass =
    align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
  return (
    <span className={`block overflow-hidden ${alignClass}`}>
      <motion.span
        className={`block ${className}`}
        initial={{ y: "108%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const Hero = ({ onNavigate }) => {
  const { t } = useLang();
  const { setCursor, reset } = useCursor();
  const ref = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const mediaScale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  // Cinematic scroll-out for the background video:
  //   - opacity: 0.8 → 0 (fades away)
  //   - clip-path insets top+bottom collapsing toward the middle → cinema close
  //   - blur ramps up subtly
  const videoOpacity = useTransform(scrollYProgress, [0, 0.35, 0.75], [0.8, 0.7, 0]);
  const clipInset = useTransform(scrollYProgress, [0, 0.4, 0.85], [0, 0, 50]);
  const clipPath = useMotionTemplate`inset(${clipInset}% 0% ${clipInset}% 0%)`;
  const blurAmount = useTransform(scrollYProgress, [0.35, 0.85], [0, 10]);
  const filter = useMotionTemplate`blur(${blurAmount}px)`;

  // Play the showreel at 2x speed (loops, muted, autoplays)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = 2;
    // Some browsers reset playbackRate after loadeddata — reapply.
    const apply = () => (v.playbackRate = 2);
    v.addEventListener("loadeddata", apply);
    v.addEventListener("play", apply);
    return () => {
      v.removeEventListener("loadeddata", apply);
      v.removeEventListener("play", apply);
    };
  }, []);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen w-full overflow-hidden">
      {/* Full-bleed looping background video (2x speed, 80% opacity, fades
          with a cinematic clip-collapse when scrolling into the timeline). */}
      <motion.div
        style={{ scale: mediaScale, clipPath, filter }}
        className="absolute inset-0 z-0 will-change-transform pointer-events-none"
      >
        <motion.video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
          style={{ opacity: videoOpacity }}
        >
          <source src="/media/hero-bg.webm" type="video/webm" />
          <source src="/media/hero-bg.mp4" type="video/mp4" />
        </motion.video>
        <div className="absolute inset-0 bg-gradient-to-b from-void/40 via-void/40 to-void/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-void/60 via-transparent to-void/30" />
      </motion.div>

      {/* Top meta bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.8 }}
        className="absolute top-24 left-0 right-0 z-30 mx-auto max-w-[1600px] px-6 md:px-12 flex items-start justify-between pointer-events-none"
      >
        <div className="flex flex-col">
          {["Motion", "Video", "Graphic"].map((w, i) => (
            <motion.span
              key={w}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 + i * 0.08, duration: 0.6, ease: EASE }}
              className="font-display text-xl md:text-2xl font-normal uppercase text-bone/45 leading-[0.95]"
            >
              {w}
            </motion.span>
          ))}
        </div>
        <span className="hidden md:block font-mono text-[11px] tracking-[0.25em] uppercase text-bone/40 pt-2">
          {t.hero.based}
        </span>
      </motion.div>

      {/* Content: statement full width. Purely decorative — must not
          intercept pointer events, otherwise the FRAMES gradient's
          mousemove listener + overflowing stroke can eat the fixed navbar's
          hamburger click as soon as the hero scrolls under it. */}
      <motion.div
        style={{ y: contentY, opacity }}
        className="relative z-10 mx-auto flex min-h-screen max-w-[1600px] items-center px-6 md:px-12 pt-48 md:pt-56 pb-24 pointer-events-none"
      >
        <div className="w-full">
          <h1 className="font-display font-medium uppercase leading-[0.82] tighter text-bone text-[16vw] sm:text-[13vw] lg:text-[10vw]">
            {STATEMENT.map((line, i) => (
              <MaskedLine key={line.text} delay={0.15 + i * 0.12} align={line.align}>
                {line.gradient ? <FramesLogo /> : line.text}
              </MaskedLine>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: EASE }}
            className="mt-8 max-w-lg font-body font-light text-lg md:text-xl text-bone/75 leading-relaxed"
          >
            {t.hero.tagline}
          </motion.p>
        </div>
      </motion.div>
    </section>
  );
};
